import React, { useEffect, useRef, useState } from 'react';
import * as $3Dmol from '3dmol';
import { chemistryApi } from '../../api/chemistry';
import MolstarViewer from './MolstarViewer';

interface StructureViewerProps {
  pdbContent?: string;
  pdbId?: string;
  uniprotId?: string;
  title?: string;
  isOpen: boolean;
  onClose: () => void;
}

type ViewStyle = 'cartoon' | 'stick' | 'sphere' | 'surface';
type ColorScheme = 'chainHetatm' | 'spectrum' | 'ssJmol' | 'residue';

export const StructureViewer: React.FC<StructureViewerProps> = ({
  pdbContent,
  pdbId,
  uniprotId,
  title,
  isOpen,
  onClose,
}) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const glviewerRef = useRef<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPdb, setCurrentPdb] = useState<string | null>(null);
  const [viewStyle, setViewStyle] = useState<ViewStyle>('cartoon');
  const [colorScheme, setColorScheme] = useState<ColorScheme>('chainHetatm');
  const [source, setSource] = useState<string>('');
  const [showMolstar, setShowMolstar] = useState(false);

  // Fetch PDB content if not provided directly
  useEffect(() => {
    const fetchStructure = async () => {
      if (pdbContent) {
        setCurrentPdb(pdbContent);
        setSource('uploaded');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        if (pdbId) {
          const response = await chemistryApi.fetchPdb(pdbId);
          setCurrentPdb(response.pdb_content);
          setSource(`RCSB PDB: ${pdbId}`);
        } else if (uniprotId) {
          const response = await chemistryApi.fetchAlphaFold(uniprotId);
          setCurrentPdb(response.pdb_content);
          setSource(`AlphaFold: ${uniprotId}`);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load structure');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && (pdbContent || pdbId || uniprotId)) {
      fetchStructure();
    }
  }, [isOpen, pdbContent, pdbId, uniprotId]);

  // Initialize and update 3Dmol viewer
  useEffect(() => {
    if (!isOpen || !currentPdb || !viewerRef.current) return;

    // Create viewer if not exists
    if (!glviewerRef.current) {
      glviewerRef.current = $3Dmol.createViewer(viewerRef.current, {
        backgroundColor: 'var(--card-bg)',
      });
    }

    const viewer = glviewerRef.current;

    // Clear and add new model
    viewer.removeAllModels();
    viewer.addModel(currentPdb, 'pdb');

    // Apply style
    applyStyle(viewer, viewStyle, colorScheme);

    viewer.zoomTo();
    viewer.render();

    return () => {
      // Cleanup on unmount
    };
  }, [isOpen, currentPdb, viewStyle, colorScheme]);

  const applyStyle = (viewer: any, style: ViewStyle, colors: ColorScheme) => {
    viewer.setStyle({}, {}); // Clear styles

    const colorSpec: any = {};
    switch (colors) {
      case 'spectrum':
        colorSpec.color = 'spectrum';
        break;
      case 'ssJmol':
        colorSpec.colorfunc = (atom: any) => {
          if (atom.ss === 'h') return 0xff0080; // Helix - pink
          if (atom.ss === 's') return 0xffc800; // Sheet - yellow
          return 0x808080; // Coil - gray
        };
        break;
      case 'residue':
        colorSpec.colorscheme = 'amino';
        break;
      default:
        colorSpec.colorscheme = 'chainHetatm';
    }

    switch (style) {
      case 'cartoon':
        viewer.setStyle({}, { cartoon: { ...colorSpec } });
        break;
      case 'stick':
        viewer.setStyle({}, { stick: { ...colorSpec, radius: 0.2 } });
        break;
      case 'sphere':
        viewer.setStyle({}, { sphere: { ...colorSpec, scale: 0.3 } });
        break;
      case 'surface':
        viewer.setStyle({}, { cartoon: { ...colorSpec, opacity: 0.5 } });
        viewer.addSurface($3Dmol.SurfaceType.VDW, { opacity: 0.7, color: 'white' });
        break;
    }

    viewer.render();
  };

  const handleSpin = () => {
    if (glviewerRef.current) {
      glviewerRef.current.spin('y', 1);
    }
  };

  const handleStopSpin = () => {
    if (glviewerRef.current) {
      glviewerRef.current.spin(false);
    }
  };

  const handleReset = () => {
    if (glviewerRef.current) {
      glviewerRef.current.zoomTo();
      glviewerRef.current.render();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-[900px] h-[700px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <h2 className="text-lg font-semibold">{title || 'Structure Viewer'}</h2>
            {source && <p className="text-sm text-gray-500">{source}</p>}
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-4 px-4 py-2 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Style:</label>
            <select
              value={viewStyle}
              onChange={(e) => setViewStyle(e.target.value as ViewStyle)}
              className="px-2 py-1 text-sm border rounded"
            >
              <option value="cartoon">Cartoon</option>
              <option value="stick">Stick</option>
              <option value="sphere">Sphere</option>
              <option value="surface">Surface</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Color:</label>
            <select
              value={colorScheme}
              onChange={(e) => setColorScheme(e.target.value as ColorScheme)}
              className="px-2 py-1 text-sm border rounded"
            >
              <option value="chainHetatm">By Chain</option>
              <option value="spectrum">Rainbow</option>
              <option value="ssJmol">Secondary Structure</option>
              <option value="residue">By Residue</option>
            </select>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={handleSpin}
              className="px-3 py-1 text-sm bg-teal-100 text-teal-700 rounded hover:bg-teal-200"
            >
              Spin
            </button>
            <button
              onClick={handleStopSpin}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Stop
            </button>
            <button
              onClick={handleReset}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Reset
            </button>
            <div className="w-px h-6 bg-gray-300 mx-2" />
            <button
              onClick={() => setShowMolstar(true)}
              className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200 font-medium"
              title="Open in Mol* for advanced analysis"
            >
              Open in Mol*
            </button>
          </div>
        </div>

        {/* Viewer */}
        <div className="flex-1 relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500"></div>
                <span className="text-gray-600">Loading structure...</span>
              </div>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
              <div className="text-center">
                <p className="text-red-500 mb-2">{error}</p>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-100 rounded hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          )}
          <div
            ref={viewerRef}
            className="w-full h-full"
            style={{ minHeight: '500px' }}
          />
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t bg-gray-50 text-xs text-gray-500">
          Mouse: Left-drag to rotate, Right-drag to translate, Scroll to zoom
        </div>
      </div>

      {/* Mol* Advanced Viewer */}
      <MolstarViewer
        isOpen={showMolstar}
        onClose={() => setShowMolstar(false)}
        pdbContent={currentPdb || undefined}
        pdbId={pdbId}
        uniprotId={uniprotId}
        title={title}
      />
    </div>
  );
};

export default StructureViewer;
